import React, {useState} from 'react';
import {Modal, Table, Button, Input, Form, ColorPicker, notification} from 'antd';
import {PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined} from '@ant-design/icons';

const StatusManagement = ({
                              open,
                              onClose,
                              statuses,
                              loading,
                              onAdd,
                              onUpdate,
                              onDelete
                          }) => {
    const [form] = Form.useForm();
    const [editingStatus, setEditingStatus] = useState(null);

    const startEditing = (status) => {
        setEditingStatus({
            id: status.id,
            name: status.name,
            color: status.color
        });
    };

    const cancelEditing = () => {
        setEditingStatus(null);
    };

    const handleAdd = async (values) => {
        const colorHex = values.color;
        await onAdd({
            name: values.name,
            color: (typeof colorHex === 'string' ? colorHex : colorHex?.toHexString())
        });
        form.resetFields();
    };

    const saveEdit = async () => {
        if (!editingStatus) return;
        await onUpdate(editingStatus.id, {
            name: editingStatus.name,
            color: editingStatus.color
        });
        setEditingStatus(null);
    };

    const confirmDelete = (status) => {
        Modal.confirm({
            title: 'Delete Status',
            content: (
                <div>
                    <p>{`Are you sure you want to delete "${status.name}"?`}</p>
                    <div className="flex items-center gap-2 mt-2 p-2 bg-gray-50 rounded-lg">
                        <div
                            className="w-4 h-4 rounded"
                            style={{backgroundColor: status.color}}
                        />
                        <span className="text-gray-600">
                            This will remove the status and may affect tasks using it
                        </span>
                    </div>
                </div>
            ),
            okText: 'Delete',
            okButtonProps: {
                danger: true,
                className: 'bg-red-500'
            },
            onOk: () => onDelete(status.id),
        });
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
                const isEditing = editingStatus?.id === record.id;
                return isEditing ? (
                    <Input
                        value={editingStatus.name}
                        onChange={(e) => setEditingStatus({
                            ...editingStatus,
                            name: e.target.value
                        })}
                        className="w-full"
                        autoFocus
                    />
                ) : (
                    <div className="flex items-center gap-2">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{backgroundColor: record.color}}
                        />
                        <span className="cursor-pointer hover:text-blue-500">
                            {text}
                        </span>
                    </div>
                );
            },
        },
        {
            title: 'Color',
            dataIndex: 'color',
            key: 'color',
            width: 200,
            render: (color, record) => {
                const isEditing = editingStatus?.id === record.id;
                return isEditing ? (
                    <ColorPicker
                        value={editingStatus.color}
                        onChange={(_color) => setEditingStatus({
                            ...editingStatus,
                            color: _color.toHexString()
                        })}
                        size="middle"
                        presets={[
                            {
                                label: 'Recommended',
                                colors: [
                                    '#1677FF', '#2ECC71', '#E74C3C',
                                    '#F1C40F', '#9B59B6', '#34495E',
                                    '#16A085', '#E67E22', '#95A5A6'
                                ],
                            },
                        ]}
                    />
                ) : (
                    <div className="flex items-center gap-2">
                        <div
                            className="w-8 h-8 rounded-lg shadow-sm"
                            style={{backgroundColor: color}}
                        />
                        {/*<span className="text-gray-500 text-sm">{color}</span>*/}
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => {
                const isEditing = editingStatus?.id === record.id;
                return isEditing ? (
                    <div className="flex items-center gap-2">
                        <Button
                            type="text"
                            icon={<CheckOutlined/>}
                            onClick={saveEdit}
                            className="hover:bg-green-50 hover:text-green-500"
                        />
                        <Button
                            type="text"
                            icon={<CloseOutlined/>}
                            onClick={cancelEditing}
                            className="hover:bg-red-50 hover:text-red-500"
                        />
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <Button
                            type="text"
                            icon={<EditOutlined/>}
                            onClick={() => startEditing(record)}
                            className="hover:bg-gray-100 hover:text-blue-500"
                        />
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined/>}
                            onClick={() => confirmDelete(record)}
                            className="hover:bg-red-50"
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <Modal
            title={
                <div className="flex items-center gap-3">
                    <span className="text-lg">Manage Statuses</span>
                    <span className="text-sm text-gray-400">
                        ({statuses.length} total)
                    </span>
                </div>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={700}
            className="rounded-xl"
        >
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                <Form
                    form={form}
                    layout="inline"
                    onFinish={handleAdd}
                    className="flex items-center gap-3"
                >
                    <Form.Item
                        name="name"
                        rules={[{required: true, message: 'Status name is required'}]}
                        className="flex-1 mb-0"
                    >
                        <Input
                            placeholder="Enter status name"
                            size="large"
                            className="rounded-lg"
                        />
                    </Form.Item>
                    <Form.Item
                        name="color"
                        rules={[{required: true, message: 'Color is required'}]}
                        className="mb-0"
                    >
                        <ColorPicker
                            size="large"
                            presets={[
                                {
                                    label: 'Recommended',
                                    colors: [
                                        '#1677FF', '#2ECC71', '#E74C3C',
                                        '#F1C40F', '#9B59B6', '#34495E',
                                        '#16A085', '#E67E22', '#95A5A6'
                                    ],
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item className="mb-0">
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            loading={loading}
                            htmlType="submit"
                            size="large"
                            className="text-sm font-semibold leading-tight px-5 py-2.5 relative items-center rounded-full justify-center border border-gray-200 bg-[#3d8bfd]"
                        >
                            Add Status
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            <Table
                columns={columns}
                dataSource={statuses}
                rowKey="id"
                pagination={false}
                loading={loading}
                className="rounded-lg border border-gray-100"
                rowClassName="hover:bg-gray-50"
            />
        </Modal>
    );
};

export default StatusManagement;
